import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { rebookResultSelectorMaker } from 'selectors/new-booking-selectors';
import { isPaxFromUserInfo, userInfoSelector } from 'selectors/app-selector';
import uuidv1 from 'uuid/v1';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import NewBookingDuck from 'ducks/new-booking-duck';
import TrackGADuck from 'ducks/track-ga-duck';
import ScribeDuck from 'ducks/scribe-duck';
import { getDeliveryID, isShopifyOrder } from 'helper/order';
import { getCompanyID } from 'helper/user';
import { reverseOrderPOIs } from 'helper/address-search';
import { shopifyBookAgainAction } from 'actions/shopify-actions';
import { pushNotificationAction } from 'actions/app-actions';
import { NOTIFICATION_TYPE } from 'components/v2/notification/notification';
import { ROUTES_V2_PATHS } from 'constants/route-constants';

const useRebook = ({ onRebook, page = 'root' }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userInfo = useSelector(userInfoSelector);
  const [totalCount, setTotalCount] = useState();
  const [rebookJobID, setRebookJobID] = useState();

  const rebookResultSelector = useMemo(
    () => rebookResultSelectorMaker(rebookJobID),
    [rebookJobID],
  );
  const rebookResult = useSelector(rebookResultSelector);

  const rebookBookings = useCallback(
    bookings => {
      if (_isEmpty(bookings)) {
        return;
      }
      const rebookJobID = uuidv1();
      setRebookJobID(rebookJobID);
      setTotalCount(bookings.length);

      if (page === 'home') {
        dispatch(
          TrackGADuck.creators.trackGAExpressHomepageWebClick('BOOK_AGAIN', {
            ORDER_ID: getDeliveryID(bookings[0]),
          }),
        );
      }
      bookings.forEach(booking => {
        dispatch(
          ScribeDuck.creators.scribeExpressBookingsWebBookAgain(booking),
        );
      });

      if (
        bookings.length === 1 &&
        isShopifyOrder(_get(bookings[0], 'thirdPartySource'))
      ) {
        const companyID = getCompanyID(userInfo);
        const isPax = isPaxFromUserInfo(userInfo);
        reverseOrderPOIs(bookings[0], companyID, isPax).then(pois => {
          dispatch(
            shopifyBookAgainAction({
              orders: _get(bookings[0], 'shopifyMetaData').map(
                ({ orderID, step_idx }) => ({
                  orderID,
                  address: _get(pois, `[${step_idx}]`),
                }),
              ),
            }),
          );
          history.push({
            pathname: ROUTES_V2_PATHS.shopifyHome,
          });
        });
        return;
      }

      dispatch(NewBookingDuck.creators.rebookWithCodes(bookings, rebookJobID));
    },
    [dispatch, setRebookJobID, userInfo],
  );

  const onRebookCallback = useCallback(
    (successCount, totalCount) => {
      if (onRebook) {
        onRebook();
      } else {
        if (successCount !== totalCount) {
          dispatch(
            pushNotificationAction({
              id: rebookJobID,
              type: NOTIFICATION_TYPE.WARNING,
              message: 'Failed to book again',
              description: `${totalCount -
                successCount} of ${totalCount} booking(s) failed`,
            }),
          );
        }
        history.push({
          pathname: ROUTES_V2_PATHS.newBooking,
        });
      }
    },
    [rebookJobID, onRebook, history],
  );

  useEffect(
    () => {
      if (rebookResult !== undefined) {
        const { successCount } = rebookResult;
        onRebookCallback(successCount, totalCount);
      }
    },
    [rebookResult, totalCount, onRebookCallback],
  );
  return rebookBookings;
};

export default useRebook;
